import { useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, UserPlus2, Plus, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Loading, Empty, Plate, VehicleStatus, DemoTag, ExportBtn, FilterBar, Field, NSelect, Chip, useFetch, V_STATUS } from "@/components/common";
import { api, errMsg, fNum, fDate } from "@/lib/api";
import { DocChip } from "@/components/Documents";

const SCOPES = [{ value: "actifs", label: "Actifs" }, { value: "archives", label: "Archivés" }, { value: "tous", label: "Tous" }];

export function VehicleTable({ manage, onEdit, onAssign, onCreate, onArchive }) {
  const [f, setF] = useState({ scope: "actifs" });
  const { data, loading, reload } = useFetch("/vehicles", f);
  VehicleTable.reload = reload;
  const restore = async (v) => {
    try {
      await api.post(`/vehicles/${v.id}/restore`);
      toast.success(`${v.plate} restauré`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <>
      <FilterBar testid="fleet-filters">
        <Field label="Affichage"><NSelect value={f.scope} onChange={(v) => setF({ ...f, scope: v })} testid="fleet-filter-scope" options={SCOPES} /></Field>
        <Field label="Type"><NSelect value={f.type} onChange={(v) => setF({ ...f, type: v })} placeholder="Tous" testid="fleet-filter-type"
          options={[{ value: "livraison", label: "Livraison" }, { value: "commercial", label: "Commercial" }]} /></Field>
        <Field label="Statut"><NSelect value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder="Tous" testid="fleet-filter-status"
          options={Object.entries(V_STATUS).filter(([k]) => k !== "archive").map(([k, v]) => ({ value: k, label: v[0] }))} /></Field>
        <div className="col-span-1 sm:col-span-2 md:col-span-3 xl:col-span-3 flex flex-wrap items-end justify-end gap-2">
          <ExportBtn path="/export/vehicles" params={{ type: f.type, scope: f.scope }} filename="vehicules.xlsx" testid="export-fleet" />
          {manage && (
            <button onClick={onCreate} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] text-sm font-bold transition-colors" data-testid="vehicle-create-button">
              <Plus className="w-4 h-4" /> Nouveau véhicule
            </button>
          )}
        </div>
      </FilterBar>
      {loading ? <Loading /> : !data?.length ? <Empty text={f.scope === "archives" ? "Aucun véhicule archivé" : "Aucune donnée"} /> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto rise">
          <table className="tbl w-full" data-testid="fleet-table">
            <thead><tr><th>Immatriculation</th><th>Type</th><th>Véhicule</th><th>Motorisation</th><th>Mise en circ.</th><th className="text-right">Km</th>
              <th>Statut</th>{manage && <th>Documents</th>}<th>Utilisateur</th><th>Financement</th><th className="text-right">Km contrat</th><th>Fin contrat</th>{manage && <th className="sticky right-0"></th>}</tr></thead>
            <tbody>
              {data.map((v) => (
                <tr key={v.id} data-testid={`fleet-row-${v.plate}`}>
                  <td>{v.type === "livraison" ? <Link to={`/livraison/vehicules/${v.id}`}><Plate>{v.plate}</Plate></Link> : <Plate>{v.plate}</Plate>}</td>
                  <td><Chip cls={v.type === "livraison" ? "bg-[#0E2D43] text-white border-[#0E2D43]" : "bg-[#FEF3E2] text-[#9a5a05] border-[#F49619]/40"}>{v.type === "livraison" ? "Livraison" : "Commercial"}</Chip></td>
                  <td className="font-semibold">{v.brand} {v.model} <DemoTag show={v.is_demo} /></td>
                  <td>{v.engine}</td><td>{fDate(v.first_registration)}</td>
                  <td className="text-right tabular-nums">{fNum(v.mileage)}</td>
                  <td><VehicleStatus v={v.status} /></td>
                  {manage && <td>{v.docs_status ? <DocChip v={v.docs_status} testid={`vehicle-docs-${v.plate}`} /> : "—"}</td>}
                  <td>{v.assigned_user_name || "—"}</td><td>{v.financing || "—"}</td>
                  <td className="text-right tabular-nums">{fNum(v.contract_km)}</td><td>{fDate(v.contract_end)}</td>
                  {manage && (
                    <td className="sticky right-0 bg-white shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)]"><div className="flex gap-1">
                      {v.status === "archive" ? (
                        <button onClick={() => restore(v)} className="p-1.5 rounded hover:bg-slate-100" title="Restaurer" data-testid={`vehicle-restore-${v.plate}`}><ArchiveRestore className="w-4 h-4" /></button>
                      ) : (<>
                      <button onClick={() => onEdit(v)} className="p-1.5 rounded hover:bg-slate-100" title="Modifier" data-testid={`vehicle-edit-${v.plate}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => onAssign(v)} className="p-1.5 rounded hover:bg-slate-100" title="Affecter" data-testid={`vehicle-assign-${v.plate}`}><UserPlus2 className="w-4 h-4" /></button>
                      <button onClick={() => onArchive(v)} className="p-1.5 rounded hover:bg-slate-100" title="Archiver" data-testid={`vehicle-archive-${v.plate}`}><Archive className="w-4 h-4" /></button>
                      </>)}
                    </div></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default function AllVehicles() {
  return (
    <div data-testid="fleet-page">
      <PageHeader overline="Flotte" title="Tous les véhicules" />
      <VehicleTable />
    </div>
  );
}
